import { useEffect, useRef, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Collapse,
  IconButton,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import messagesIllustration from "resources/images/messages-illustration.png";
import { MediumCustomIcon } from "../../layer00";
import { EmptyStateCard } from "../../layer01";
import { IssueCard, CardPaginationBar } from "../../layer20";
import { useGetCriticalIssuesQuery } from "app/containers/TeamsTab/teamsCheckInApi";
import { MIN_CRITICAL_CONCERN_LEVEL } from "app/containers/TeamsTab/helpers";
import { useAppSelector } from "utils/redux/hooks";
import {
  selectAllCompanyUsersById,
  selectSampleUsersInfoById,
} from "app/containers/Global/slice";

const PAGE_SIZE = 3;

export type IssueListProps = {};

export const IssueList = (_props: IssueListProps) => {
  const theme = useTheme();
  const [open, setOpen] = useState(true);
  const [page, setPage] = useState(0);

  const {
    data: issues = [],
    isLoading,
    isSuccess,
  } = useGetCriticalIssuesQuery({
    showDismissed: false,
  });

  // Collapse once the first load comes back empty; after that the user controls it.
  const autoCollapseChecked = useRef(false);
  useEffect(() => {
    if (!isSuccess || autoCollapseChecked.current) return;
    autoCollapseChecked.current = true;
    if (issues.length === 0) setOpen(false);
  }, [isSuccess, issues.length]);

  const sampleUsersById = useAppSelector(selectSampleUsersInfoById);
  const companyUsersById = useAppSelector(selectAllCompanyUsersById);
  const usersById = { ...sampleUsersById, ...companyUsersById };

  const totalPages = Math.max(1, Math.ceil(issues.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages - 1);
  const visibleIssues = issues.slice(
    safePage * PAGE_SIZE,
    (safePage + 1) * PAGE_SIZE
  );
  const getCardBody = () => {
    if (isLoading) {
      return (
        <Box px="1rem">
          <Skeleton
            variant="rounded"
            height={96}
            sx={{ borderRadius: "1rem" }}
          />
        </Box>
      );
    }
    if (issues.length === 0) {
      return (
        <EmptyStateCard
          image={messagesIllustration}
          title="No issues to investigate"
          body="You have no issues to investigate right now."
        />
      );
    }
    return (
      <Stack className="flex flex-col gap-[1rem]">
        {visibleIssues.map((issue) => {
          const user = usersById[issue.userAccountId];
          const personName = user
            ? `${user.firstName} ${user.lastName}`
            : undefined;

          return (
            <IssueCard
              key={issue.threadId}
              threadId={issue.threadId}
              userAccountId={issue.userAccountId}
              personName={personName}
              state={
                issue.concernLevel === undefined ||
                (issue.concernLevel ?? 0) >= MIN_CRITICAL_CONCERN_LEVEL
                  ? "error"
                  : "warning"
              }
              comment={issue.explanationOfConcern ?? ""}
              date={issue.created_at}
            />
          );
        })}
      </Stack>
    );
  };

  return (
    <Card>
      <CardHeader
        disableTypography
        className={open ? "border-b border-[#8080801f]" : ""}
        onClick={() => setOpen((prev) => !prev)}
        sx={{ cursor: "pointer" }}
        title={
          <Stack direction="row" alignItems="center" columnGap=".625rem">
            <Typography variant="cardTitle">Issues to investigate</Typography>
            {issues.length > 0 && (
              <Chip
                label={issues.length}
                size="small"
                variant="error"
                sx={{
                  height: "1.25rem",
                  "& .MuiChip-label": { paddingX: ".5rem", fontSize: ".75rem" },
                }}
              />
            )}
          </Stack>
        }
        action={
          <IconButton sx={{ border: "none" }}>
            <MediumCustomIcon
              icon="CHEVRONUP"
              stroke={theme.palette.secondary.light}
              rotated={!open}
            />
          </IconButton>
        }
      />
      <Collapse in={open}>
        <CardContent
          sx={{
            background: "none",
            paddingLeft: 0,
            paddingRight: 0,
            paddingBottom: 0,
          }}
        >
          {getCardBody()}
          {!isLoading && totalPages > 1 && (
            <CardPaginationBar
              page={safePage}
              totalPages={totalPages}
              onNavigate={setPage}
            />
          )}
        </CardContent>
      </Collapse>
    </Card>
  );
};
